import { Slot } from '@radix-ui/react-slot';
import type { ComponentPropsWithRef, ReactNode } from 'react';

import { cx } from '@/shared/lib/cx';

import styles from './Button.module.scss';

/**
 * Начертание — насколько громко кнопка звучит.
 *
 * `solid` — главное действие экрана, на экране обычно одно.
 * `outline` — равноправное действие рядом с главным.
 * `ghost` — действие в плотном окружении: строка таблицы, панель редактора.
 * `dashed` — приглашение что-то добавить: «Добавить сайт», «Добавить поле».
 * `link` — действие внутри текста или в тосте.
 */
export type ButtonVariant = 'solid' | 'outline' | 'ghost' | 'dashed' | 'link';

/**
 * Тон — смысл действия, а не его громкость.
 *
 * Две оси намеренно разделены: «удалить» в строке таблицы и «удалить
 * навсегда» в диалоге подтверждения — это один тон в разных начертаниях,
 * а не две несвязанные кнопки.
 */
export type ButtonTone = 'accent' | 'neutral' | 'danger';

export type ButtonSize = 'sm' | 'md' | 'lg';

const VARIANT: Record<ButtonVariant, string> = {
  solid: styles['button--solid'] ?? '',
  outline: styles['button--outline'] ?? '',
  ghost: styles['button--ghost'] ?? '',
  dashed: styles['button--dashed'] ?? '',
  link: styles['button--link'] ?? '',
};

const TONE: Record<ButtonTone, string> = {
  accent: styles['button--accent'] ?? '',
  neutral: styles['button--neutral'] ?? '',
  danger: styles['button--danger'] ?? '',
};

const SIZE: Record<ButtonSize, string> = {
  sm: styles['button--sm'] ?? '',
  md: styles['button--md'] ?? '',
  lg: styles['button--lg'] ?? '',
};

interface BaseProps {
  variant?: ButtonVariant;
  tone?: ButtonTone;
  size?: ButtonSize;
  /** Иконка перед текстом. При загрузке подменяется индикатором. */
  iconLeft?: ReactNode;
  /** Иконка после текста — например, шеврон у кнопки с меню. */
  iconRight?: ReactNode;
  fullWidth?: boolean;
  /**
   * Действие выполняется. Кнопка блокируется и помечается `aria-busy`,
   * но текст остаётся на месте — иначе ширина кнопки прыгает и соседние
   * элементы дёргаются в момент нажатия.
   */
  loading?: boolean;
  /** Кнопка с памятью: «жирный» и «курсив» в панели редактора. */
  pressed?: boolean;
  /**
   * Отрисовать переданный элемент вместо `<button>` — например, ссылку
   * роутера. Внутреннее наполнение при этом собирает вызывающий код:
   * иконки и индикатор загрузки здесь не подставляются.
   */
  asChild?: boolean;
}

// С ref, а не без: кнопке нужно уметь принимать ссылку — например, чтобы
// диалог подтверждения увёл на неё фокус при открытии.
type NativeButtonProps = Omit<ComponentPropsWithRef<'button'>, 'color'>;

/** Кнопка с текстом. */
type TextButtonProps = BaseProps &
  NativeButtonProps & {
    iconOnly?: false;
    children: ReactNode;
  };

/**
 * Кнопка без текста: содержимое — сама иконка.
 *
 * `aria-label` требуется на уровне типов: иконка без подписи невидима для
 * скринридера, а забыть подпись легко — так забыть не получится, сборка не
 * пройдёт.
 */
type IconButtonProps = BaseProps &
  NativeButtonProps & {
    iconOnly: true;
    'aria-label': string;
    children: ReactNode;
  };

export type ButtonProps = TextButtonProps | IconButtonProps;

function Spinner() {
  return (
    <svg
      className={styles['button__spinner']}
      width="14"
      height="14"
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden="true"
    >
      <circle cx="8" cy="8" r="6.5" stroke="currentColor" strokeWidth="2" opacity="0.25" />
      <path
        d="M14.5 8a6.5 6.5 0 0 0-6.5-6.5"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}

export function Button({
  variant = 'solid',
  tone = 'accent',
  size = 'md',
  iconLeft,
  iconRight,
  iconOnly = false,
  fullWidth = false,
  loading = false,
  pressed,
  asChild = false,
  className,
  disabled,
  type,
  children,
  ...rest
}: ButtonProps) {
  const classes = cx(
    styles['button'],
    VARIANT[variant],
    TONE[tone],
    SIZE[size],
    iconOnly && styles['button--icon-only'],
    fullWidth && styles['button--full'],
    loading && styles['button--loading'],
    pressed && styles['button--pressed'],
    className,
  );

  if (asChild) {
    return (
      <Slot className={classes} data-pressed={pressed ? '' : undefined} {...rest}>
        {children}
      </Slot>
    );
  }

  const leading = loading ? <Spinner /> : iconLeft;

  return (
    <button
      // Без явного type кнопка внутри формы по умолчанию отправляет её —
      // источник неожиданных сабмитов при клике на «Отмена».
      type={type ?? 'button'}
      className={classes}
      disabled={disabled === true || loading}
      aria-busy={loading || undefined}
      aria-pressed={pressed}
      {...rest}
    >
      {leading !== undefined && leading !== null && (
        <span className={styles['button__icon']}>{leading}</span>
      )}
      {children}
      {iconRight !== undefined && iconRight !== null && (
        <span className={styles['button__icon']}>{iconRight}</span>
      )}
    </button>
  );
}
